import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { A11y, Pagination } from 'swiper/modules';
import ImageLightbox from './ImageLightbox';
import 'swiper/css';
import 'swiper/css/pagination';
import '../../assets/styles/product.image.css';

const ProductImage = ({ src, images = [], alt, className, sideThumbnails = true, children, zoomTargetRef }) => {
    const [activeImage, setActiveImage] = useState(src);
    const [isZoomed, setIsZoomed] = useState(false);
    const [lensStyle, setLensStyle] = useState({});
    const [zoomStyle, setZoomStyle] = useState({});
    const [portalRect, setPortalRect] = useState(null);
    const [showLightbox, setShowLightbox] = useState(false);
    
    const containerRef = useRef(null);
    const mainImgRef = useRef(null);
    const imgRef = useRef(null);
    const mobileSwiperRef = useRef(null);

    const allImages = useMemo(() => {
        const uniqueParamImages = [...new Set((images || []).filter(Boolean))];
        if (src && !uniqueParamImages.includes(src)) {
            return [src, ...uniqueParamImages];
        }

        return uniqueParamImages.length > 0 ? uniqueParamImages : [src || '/images/default.jpg'];
    }, [images, src]);

    const activeIndex = Math.max(allImages.indexOf(activeImage), 0);

    // Update active image if src prop changes (e.g. from variation selector)
    useEffect(() => {
        setActiveImage(src);
    }, [src]);

    useEffect(() => {
        if (!allImages.length) return;

        if (!allImages.includes(activeImage)) {
            setActiveImage(allImages[0]);
        }
    }, [activeImage, allImages]);

    useEffect(() => {
        if (!mobileSwiperRef.current || mobileSwiperRef.current.activeIndex === activeIndex) return;

        mobileSwiperRef.current.slideTo(activeIndex, 0);
    }, [activeIndex]);

    const calculatePortalRect = useCallback(() => {
        if (!mainImgRef.current) return null;

        const imgRect = mainImgRef.current.getBoundingClientRect();

        if (zoomTargetRef && zoomTargetRef.current) {
            const targetRect = zoomTargetRef.current.getBoundingClientRect();
            return {
                top: targetRect.top + window.scrollY,
                left: targetRect.left + window.scrollX,
                width: targetRect.width,
                height: targetRect.height
            };
        }

        // Default fallback: to the right of the image
        return {
            top: imgRect.top + window.scrollY,
            left: imgRect.left + imgRect.width + 20 + window.scrollX,
            width: imgRect.width,
            height: imgRect.height
        };
    }, [zoomTargetRef]);

    const handleMouseMove = (e) => {
        if (!mainImgRef.current || !imgRef.current || !isZoomed) return;

        const containerRect = mainImgRef.current.getBoundingClientRect();
        const imgRect = imgRef.current.getBoundingClientRect();

        // Mouse relative to CONTAINER (for lens positioning)
        const mouseXContainer = e.pageX - containerRect.left - window.scrollX;
        const mouseYContainer = e.pageY - containerRect.top - window.scrollY;

        const currentPortalRect = calculatePortalRect();
        if (!currentPortalRect) return;

        // Base lens size (how much of the main image we show)
        // We want the zoom to be at least 2x
        const zoomLevel = 2.25;
        const lensWidth = currentPortalRect.width / zoomLevel;
        const lensHeight = currentPortalRect.height / zoomLevel;

        // Lens position (centered on mouse)
        let lensLeft = mouseXContainer - lensWidth / 2;
        let lensTop = mouseYContainer - lensHeight / 2;

        // Constraints relative to CONTAINER
        const imgOffsetLeft = imgRect.left - containerRect.left;
        const imgOffsetTop = imgRect.top - containerRect.top;

        const minLensLeft = imgOffsetLeft;
        const maxLensLeft = imgOffsetLeft + imgRect.width - lensWidth;
        const minLensTop = imgOffsetTop;
        const maxLensTop = imgOffsetTop + imgRect.height - lensHeight;

        lensLeft = Math.max(minLensLeft, Math.min(lensLeft, maxLensLeft));
        lensTop = Math.max(minLensTop, Math.min(lensTop, maxLensTop));

        setLensStyle({
            left: `${lensLeft}px`,
            top: `${lensTop}px`,
            width: `${lensWidth}px`,
            height: `${lensHeight}px`,
        });

        // Zoom background position (Relative to IMAGE)
        const lensRelativeX = lensLeft - imgOffsetLeft;
        const lensRelativeY = lensTop - imgOffsetTop;

        const bgWidth = imgRect.width * zoomLevel;
        const bgHeight = imgRect.height * zoomLevel;

        setZoomStyle({
            backgroundImage: `url(${activeImage})`,
            backgroundSize: `${bgWidth}px ${bgHeight}px`,
            backgroundPosition: `-${lensRelativeX * zoomLevel}px -${lensRelativeY * zoomLevel}px`,
            width: '100%',
            height: '100%',
            backgroundRepeat: 'no-repeat'
        });

        // Update portal position
        setPortalRect(currentPortalRect);
    };

    const handleMouseEnter = () => {
        if (window.innerWidth >= 992 && mainImgRef.current) {
            const rect = calculatePortalRect();
            setPortalRect(rect);
            setIsZoomed(true);
        }
    };
    
    // Add effect to handle window resize/scroll to update portal rect
    useEffect(() => {
        if (!isZoomed) return;

        const updateRect = () => {
            const rect = calculatePortalRect();
            setPortalRect(rect);
        };

        window.addEventListener('resize', updateRect);
        window.addEventListener('scroll', updateRect, true);

        return () => {
            window.removeEventListener('resize', updateRect);
            window.removeEventListener('scroll', updateRect, true);
        };
    }, [isZoomed, calculatePortalRect]);

    const handleMouseLeave = () => {
        setIsZoomed(false);
        setPortalRect(null);
    };

    const handleImageClick = (image = activeImage) => {
        setActiveImage(image);
        setShowLightbox(true);
    };

    return (
        <div className={`product-image-container ${sideThumbnails ? 'with-side-thumbnails' : ''} ${className || ''}`} ref={containerRef}>
            {/* Main Image Display */}
            <div 
                className={`product-image-main product-image-main--desktop ${isZoomed ? 'is-zoomed' : ''}`}
                ref={mainImgRef}
                onClick={() => handleImageClick(activeImage)}
            >
                <img
                    ref={imgRef}
                    src={activeImage}
                    alt={alt}
                    loading="lazy"
                    onMouseMove={handleMouseMove}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = '/images/default.jpg';
                    }}
                />
                
                {children}
                
                {isZoomed && (
                    <div className="product-image-lens" style={lensStyle} />
                )}
            </div>

            <div className="product-image-main product-image-main--mobile">
                <Swiper
                    modules={[Pagination, A11y]}
                    pagination={
                        allImages.length > 1
                            ? {
                                  clickable: true,
                              }
                            : false
                    }
                    initialSlide={activeIndex}
                    onSwiper={(swiper) => {
                        mobileSwiperRef.current = swiper;
                    }}
                    onSlideChange={(swiper) => {
                        const nextImage = allImages[swiper.activeIndex];
                        if (nextImage) {
                            setActiveImage(nextImage);
                        }
                    }}
                    className="product-image-mobile-swiper"
                >
                    {allImages.map((image, index) => (
                        <SwiperSlide key={`${image}-${index}`} className="product-image-mobile-slide">
                            <button
                                type="button"
                                className="product-image-mobile-button"
                                onClick={() => handleImageClick(image)}
                                aria-label={`Abrir imagen ${index + 1} de ${allImages.length}`}
                            >
                                <img
                                    src={image}
                                    alt={`${alt} ${index + 1}`}
                                    loading="lazy"
                                    onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = '/images/default.jpg';
                                    }}
                                />
                            </button>
                        </SwiperSlide>
                    ))}
                </Swiper>
            </div>

            {/* Zoom Portal - Rendered via React Portal to body to avoid overflow issues */}
            {isZoomed && portalRect && createPortal(
                <div 
                    className={`product-image-zoom-portal ${portalRect ? 'active' : ''}`} 
                    style={{
                        top: `${portalRect.top}px`,
                        left: `${portalRect.left}px`,
                        width: `${portalRect.width}px`,
                        height: `${portalRect.height}px`,
                        position: 'absolute'
                    }}
                >
                    <div style={zoomStyle} />
                </div>,
                document.body
            )}

            {/* Thumbnails Gallery */}
            {allImages.length > 1 && (
                <div className="product-image-thumbnails">
                    {allImages.map((img, index) => (
                        <div
                            key={index}
                            className={`product-thumb-item ${activeImage === img ? 'active' : ''}`}
                            onMouseEnter={() => setActiveImage(img)}
                            onClick={() => setActiveImage(img)}
                        >
                            <img src={img} alt={`${alt} - ${index}`} />
                        </div>
                    ))}
                </div>
            )}

            <ImageLightbox
                show={showLightbox}
                onHide={() => setShowLightbox(false)}
                src={activeImage}
                images={allImages}
                initialIndex={activeIndex}
                onImageChange={setActiveImage}
                alt={alt}
            />
        </div>
    );
};

export default ProductImage;
